import { useCallback, useEffect, useRef } from "react";

// Mirra easing + timing tokens (ported from mirra-motion.html)
export const EASE = {
  out: "cubic-bezier(.2,.8,.2,1)",
  std: "cubic-bezier(.4,0,.2,1)",
  inOut: "cubic-bezier(.7,0,.2,1)",
  spring: "cubic-bezier(.2,1.6,.4,1)",
  overshoot: "cubic-bezier(.2,.9,.2,1.25)",
};

// rAF loop that runs only while mounted, on screen and the tab is visible.
export function useRafLoop(ref, onFrame, enabled = true) {
  const cb = useRef(onFrame);
  cb.current = onFrame;
  useEffect(() => {
    if (!enabled || !ref.current) return undefined;
    let raf = 0;
    let onScreen = true;
    let last = performance.now();
    const tick = (t) => {
      cb.current(t, t - last);
      last = t;
      raf = requestAnimationFrame(tick);
    };
    const sync = () => {
      const run = onScreen && document.visibilityState === "visible";
      if (run && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };
    const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; sync(); });
    io.observe(ref.current);
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", sync); if (raf) cancelAnimationFrame(raf); };
  }, [ref, enabled]);
}

// Timers that are all cleared on unmount.
export function useTimers() {
  const ids = useRef(new Set());
  useEffect(() => () => { ids.current.forEach((id) => { clearTimeout(id); clearInterval(id); }); ids.current.clear(); }, []);
  const timeout = useCallback((fn, ms) => { const id = setTimeout(() => { ids.current.delete(id); fn(); }, ms); ids.current.add(id); return id; }, []);
  const interval = useCallback((fn, ms) => { const id = setInterval(fn, ms); ids.current.add(id); return id; }, []);
  const clear = useCallback((id) => { clearTimeout(id); clearInterval(id); ids.current.delete(id); }, []);
  return { timeout, interval, clear };
}

// Returns a function that allows at most `perSecond` flashes per second (per caller/row).
export function useFlashThrottle(perSecond = 4) {
  const last = useRef(0);
  return useCallback(() => {
    const now = performance.now();
    if (now - last.current < 1000 / perSecond) return false;
    last.current = now;
    return true;
  }, [perSecond]);
}
